<template>
  <div class="card mb-4">
    <div class="card-header d-flex justify-content-between align-items-center">
      <h5 class="mb-0">Issuance Production Data</h5>
      <!-- Status Filter -->
      <select
        v-model="statusFilter"
        class="form-select form-select-sm"
        style="width: 200px"
      >
        <option value="">All Statuses</option>
        <option :value="IssuanceProductionStatus.PENDING">Pending</option>
        <option :value="IssuanceProductionStatus.SUBMITTED">Submitted</option>
        <option :value="IssuanceProductionStatus.ACCEPTED">Accepted</option>
        <option :value="IssuanceProductionStatus.COMPLETED">Completed</option>
        <option :value="IssuanceProductionStatus.REJECTED">Rejected</option>
      </select>
    </div>

    <div class="card-body">
      <!-- Loading State -->
      <LoadingIndicatorComponent v-if="loading" />

      <!-- Error State -->
      <div v-else-if="error" class="alert alert-danger">
        <font-awesome-icon icon="fas fa-exclamation-triangle" class="me-2" />
        {{ error }}
        <button class="btn btn-outline-danger btn-sm ms-2" @click="fetchData">
          Retry
        </button>
      </div>

      <!-- Table -->
      <div v-else-if="companiesData.length > 0" class="table-responsive">
        <table class="table">
          <thead class="table-light">
            <tr>
              <th width="30"></th>
              <th width="40">
                <input
                  type="checkbox"
                  class="form-check-input"
                  :checked="allSelectableSelected"
                  @change="toggleSelectAll"
                  :disabled="selectableRecordIds.length === 0"
                  title="Select all eligible records"
                />
              </th>
              <th>Company</th>
              <th>Devices</th>
              <th>Total (MWh)</th>
              <th>Status Summary</th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="company in companiesData"
              :key="company.company_id"
            >
              <!-- Company Row -->
              <tr
                class="company-row"
                :class="{
                  'table-active': expandedCompanies.has(company.company_id),
                }"
              >
                <td
                  @click="toggleCompany(company.company_id)"
                  style="cursor: pointer"
                >
                  <font-awesome-icon
                    :icon="
                      expandedCompanies.has(company.company_id)
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-right'
                    "
                  />
                </td>
                <td></td>
                <td
                  @click="toggleCompany(company.company_id)"
                  style="cursor: pointer"
                >
                  <strong>{{ company.company_name }}</strong>
                </td>
                <td
                  @click="toggleCompany(company.company_id)"
                  style="cursor: pointer"
                >
                  {{ company.device_count }} devices
                </td>
                <td
                  @click="toggleCompany(company.company_id)"
                  style="cursor: pointer"
                >
                  {{ formatMwh(company.total_mwh) }}
                </td>
                <td
                  @click="toggleCompany(company.company_id)"
                  style="cursor: pointer"
                >
                  <span
                    v-for="(count, status) in company.status_summary"
                    :key="status"
                    class="badge me-1"
                    :class="getStatusBadgeClass(status)"
                  >
                    {{ count }} {{ capitalize(status) }}
                  </span>
                </td>
              </tr>

              <!-- Device Rows (Expandable) -->
              <tr v-show="expandedCompanies.has(company.company_id)">
                <td colspan="6" class="p-0">
                  <div class="nested-table-container">
                    <table class="table table-sm table-hover mb-0">
                      <thead>
                        <tr>
                          <th width="40"></th>
                          <th>Device Name</th>
                          <th>GSRN/Device Identification Code</th>
                          <th>ESO Object Number</th>
                          <th>Value (MWh)</th>
                          <th>Period</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="device in company.devices"
                          :key="device.subrow_id"
                        >
                          <td>
                            <input
                              v-if="device.is_selectable"
                              type="checkbox"
                              class="form-check-input"
                              :checked="selectedRecordIds.has(device.subrow_id)"
                              @change="toggleRecordSelection(device.subrow_id)"
                            />
                          </td>
                          <td>{{ device.device_name }}</td>
                          <td>
                            <code>{{ device.device_eic }}</code>
                          </td>
                          <td>{{ device.eso_object_number || "-" }}</td>
                          <td>{{ formatMwh(device.value) }}</td>
                          <td>{{ formatPeriod(device.period_start_at) }}</td>
                          <td>
                            <span
                              class="badge"
                              :class="getStatusBadgeClass(device.status)"
                            >
                              {{ capitalize(device.status) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-5">
        <font-awesome-icon
          icon="fas fa-inbox"
          size="3x"
          class="text-muted mb-3"
        />
        <h5 class="text-muted">No Data Available</h5>
        <p class="text-muted">
          No issuance production data found for the selected month.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { IssuanceProductionStatus } from "@/dashboard/production_strategy/issuance-reporting/issuanceStatusUtils";
import { capitalize } from "lodash-es";
import { computed, onMounted, ref, watch } from "vue";
import { getIssuanceDataByMonth } from "./api";
import type {
  AdminIssuanceProductionDataResponse,
  CompanyRecords,
} from "./types";
import { formatMwh, formatPeriod, getStatusBadgeClass } from "./utils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

// Props
const props = defineProps<{
  year: number;
  month: number;
}>();

// Emits
const emit = defineEmits<{
  "update:selected-records": [companyRecords: CompanyRecords[]];
}>();

// Global error handler
const { globalErrorHandler } = useGlobalHandlers();

// State
const loading = ref(false);
const error = ref<string | null>(null);
const rawData = ref<AdminIssuanceProductionDataResponse | null>(null);
const statusFilter = ref("");
const expandedCompanies = ref(new Set<number>());
const selectedRecordIds = ref(new Set<number>());

// Computed: Group data by company
const companiesData = computed(() => {
  if (!rawData.value) return [];

  const data = rawData.value;

  // Group mainRows by company_id
  const companyMap = new Map<
    number,
    {
      company_id: number;
      company_name: string;
      device_count: number;
      total_mwh: number;
      status_summary: Record<string, number>;
      devices: Array<{
        subrow_id: number;
        device_name: string;
        device_eic: string;
        eso_object_number: string | null;
        value: number;
        period_start_at: string;
        status: string;
        is_selectable: boolean;
      }>;
    }
  >();

  data.issuance_data_main_rows.forEach((mainRow) => {
    const companyId = mainRow.registry_device.company_id;

    mainRow.subrows.forEach((subrow) => {
      // Apply client-side status filter (backend always returns all statuses)
      if (statusFilter.value && subrow.status !== statusFilter.value) return;

      if (!companyMap.has(companyId)) {
        // Find company name from the companies list, fallback to company_id
        const company = data.companies.find((c) => c.id === companyId);
        const companyName = company?.name || `Company ${companyId}`;

        companyMap.set(companyId, {
          company_id: companyId,
          company_name: companyName,
          device_count: 0,
          total_mwh: 0,
          status_summary: {},
          devices: [],
        });
      }

      const company = companyMap.get(companyId)!;
      company.total_mwh += subrow.value;
      company.status_summary[subrow.status] =
        (company.status_summary[subrow.status] || 0) + 1;

      // Only pending, submitted, accepted, and rejected statuses can be selected for sending to Grexel
      // Rejected records can be corrected and resubmitted
      const isSelectable =
        subrow.status === IssuanceProductionStatus.PENDING ||
        subrow.status === IssuanceProductionStatus.SUBMITTED ||
        subrow.status === IssuanceProductionStatus.ACCEPTED ||
        subrow.status === IssuanceProductionStatus.REJECTED;

      company.devices.push({
        subrow_id: subrow.id,
        device_name: mainRow.registry_device.device_name || "Unknown",
        device_eic: mainRow.registry_device.device_eic || "N/A",
        eso_object_number: subrow.external_id || null,
        value: subrow.value,
        period_start_at: subrow.period_start_at,
        status: subrow.status,
        is_selectable: isSelectable,
      });
    });

    // Update device count
    const company = companyMap.get(companyId);
    if (company) {
      const uniqueDevices = new Set(company.devices.map((d) => d.device_name));
      company.device_count = uniqueDevices.size;
    }
  });

  return Array.from(companyMap.values());
});

// Computed: All selectable record IDs
const selectableRecordIds = computed(() => {
  const ids: number[] = [];
  companiesData.value.forEach((company) => {
    company.devices.forEach((device) => {
      if (device.is_selectable) {
        ids.push(device.subrow_id);
      }
    });
  });
  return ids;
});

// Computed: Are all selectable records selected?
const allSelectableSelected = computed(() => {
  const selectable = selectableRecordIds.value;
  if (selectable.length === 0) return false;
  return selectable.every((id) => selectedRecordIds.value.has(id));
});

// Methods
const fetchData = async () => {
  loading.value = true;
  error.value = null;

  try {
    // Always fetch all statuses from backend, filter client-side only
    rawData.value = await getIssuanceDataByMonth(props.year, props.month);
  } catch (err: any) {
    error.value =
      err.response?.data?.message || err.message || "Failed to load data";
    globalErrorHandler(err);
  } finally {
    loading.value = false;
  }
};

const toggleCompany = (companyId: number) => {
  if (expandedCompanies.value.has(companyId)) {
    expandedCompanies.value.delete(companyId);
  } else {
    expandedCompanies.value.add(companyId);
  }
};

const toggleSelectAll = () => {
  selectedRecordIds.value = allSelectableSelected.value
    ? new Set()
    : new Set(selectableRecordIds.value);
  emitSelection();
};

const toggleRecordSelection = (recordId: number) => {
  if (selectedRecordIds.value.has(recordId)) {
    selectedRecordIds.value.delete(recordId);
  } else {
    selectedRecordIds.value.add(recordId);
  }
  emitSelection();
};

const emitSelection = () => {
  // Group selected records by company
  const companyRecordsMap = new Map<number, number[]>();

  companiesData.value.forEach((company) => {
    const selectedInCompany = company.devices
      .filter((device) => selectedRecordIds.value.has(device.subrow_id))
      .map((device) => device.subrow_id);

    if (selectedInCompany.length > 0) {
      companyRecordsMap.set(company.company_id, selectedInCompany);
    }
  });

  const companyRecords: CompanyRecords[] = Array.from(
    companyRecordsMap.entries()
  ).map(([companyId, recordIds]) => ({
    company_id: companyId,
    record_ids: recordIds,
  }));

  emit("update:selected-records", companyRecords);
};

// Watchers
watch([() => props.year, () => props.month], () => {
  fetchData();
  expandedCompanies.value.clear();
  selectedRecordIds.value.clear();
  emitSelection();
});

watch(statusFilter, () => {
  // No need to refetch, computed will handle filtering
  // Clear selection when filter changes
  selectedRecordIds.value.clear();
  emitSelection();
});

// Lifecycle
onMounted(() => {
  fetchData();
});
</script>

<style scoped lang="scss">
.company-row {
  border-left: 3px solid var(--phoenix-primary);
  background-color: var(--phoenix-body-highlight-bg);

  &:hover {
    background-color: var(--phoenix-primary-bg-subtle);
  }

  &.table-active {
    background-color: var(--phoenix-primary-bg-subtle);
  }
}

.nested-table-container {
  background-color: var(--bs-body-bg);
  border-top: 1px solid var(--bs-border-color);
}

.table-sm {
  font-size: 0.875rem;
}

code {
  font-size: 0.875em;
  color: var(--bs-secondary);
}

.badge {
  font-size: 0.75em;
}
</style>
